import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MdShare, MdContentCopy, MdCheck, MdAccessTime } from 'react-icons/md';
import { FaXTwitter, FaFacebook } from 'react-icons/fa6';
import axios from '../config/api';
import { API_URL } from '../config/api';

function ShareButton({ pollId, pollQuestion, isCustomPoll = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(null); // 'copy', 'x', 'facebook'
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const isOpenRef = useRef(false);
  
  const shareText = `Check out this poll: "${pollQuestion.substring(0, 100)}${pollQuestion.length > 100 ? '...' : ''}"`;

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 280;
      
      let left = rect.left + window.scrollX;
      if (left + dropdownWidth > window.innerWidth) {
        left = rect.right - dropdownWidth + window.scrollX;
      }
      if (left < 10) {
        left = 10;
      }
      
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: left
      });
    }
  }, [isOpen]);

  const handleClickOutside = useCallback((event) => {
    if (!isOpenRef.current) return;
    
    const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
    const clickedOutsideButton = buttonRef.current && !buttonRef.current.contains(event.target);
    
    if (clickedOutsideDropdown && clickedOutsideButton) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, handleClickOutside]);

  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
      setSharing(null);
    }
  }, [isOpen]);

  const handleToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(prev => !prev);
  };

  // Create snapshot on-demand and execute share action
  const createSnapshotAndShare = async (action) => {
    setSharing(action);
    
    try {
      const response = await axios.post(`${API_URL}/api/snapshots/create`, {
        poll_id: pollId,
        poll_type: isCustomPoll ? 'custom' : 'system'
      });
      
      const snapshotUrl = response.data.snapshotUrl;
      const timestamp = new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      // Execute the share action
      if (action === 'copy') {
        try {
          await navigator.clipboard.writeText(snapshotUrl);
        } catch (err) {
          const textArea = document.createElement('textarea');
          textArea.value = snapshotUrl;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
          setSharing(null);
        }, 2000);
      } else if (action === 'x') {
        const text = `${shareText} (Results as of ${timestamp})`;
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(snapshotUrl)}`;
        window.open(twitterUrl, '_blank', 'width=550,height=420');
        setIsOpen(false);
        setSharing(null);
      } else if (action === 'facebook') {
        const text = `${shareText} (Results as of ${timestamp})`;
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(snapshotUrl)}&quote=${encodeURIComponent(text)}`;
        window.open(facebookUrl, '_blank', 'width=550,height=420');
        setIsOpen(false);
        setSharing(null);
      }
    } catch (err) {
      console.error('Failed to create snapshot:', err);
      alert('Failed to share. Please try again.');
      setSharing(null);
    }
  };

  const dropdown = isOpen ? createPortal(
    <div 
      ref={dropdownRef}
      className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[9999]"
      style={{ 
        top: dropdownPosition.top, 
        left: dropdownPosition.left,
        width: '280px'
      }}
      onClick={(e) => e.stopPropagation()}
      data-testid="share-dropdown"
    >
      {/* Header */}
      <div className="px-4 py-2 border-b border-gray-100">
        <p className="text-sm font-semibold text-gray-800 font-rubik">Share Poll Results</p>
        <p className="text-xs text-gray-400 font-rubik flex items-center gap-1 mt-0.5">
          <MdAccessTime className="text-xs" />
          Snapshot created when you share
        </p>
      </div>

      {/* Copy Link */}
      <button
        onClick={(e) => { e.stopPropagation(); createSnapshotAndShare('copy'); }}
        disabled={sharing !== null}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors disabled:opacity-60"
        data-testid="copy-link-button"
      >
        {sharing === 'copy' ? (
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        ) : copied ? (
          <MdCheck className="text-lg text-primary" />
        ) : (
          <MdContentCopy className="text-lg text-gray-600" />
        )}
        <span className="text-sm font-medium font-rubik text-gray-700">
          {sharing === 'copy' ? 'Creating...' : copied ? 'Link copied!' : 'Copy snapshot link'}
        </span>
      </button>

      {/* Share to X */}
      <button
        onClick={(e) => { e.stopPropagation(); createSnapshotAndShare('x'); }}
        disabled={sharing !== null}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors disabled:opacity-60"
        data-testid="share-twitter-button"
      >
        {sharing === 'x' ? (
          <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
        ) : (
          <FaXTwitter className="text-lg text-black" />
        )}
        <span className="text-sm font-medium font-rubik text-gray-700">
          {sharing === 'x' ? 'Creating...' : 'Share on X'}
        </span>
      </button>

      {/* Share to Facebook */}
      <button
        onClick={(e) => { e.stopPropagation(); createSnapshotAndShare('facebook'); }}
        disabled={sharing !== null}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors disabled:opacity-60"
        data-testid="share-facebook-button"
      >
        {sharing === 'facebook' ? (
          <div className="w-5 h-5 border-2 border-[#4267B2] border-t-transparent rounded-full animate-spin" />
        ) : (
          <FaFacebook className="text-lg text-[#4267B2]" />
        )}
        <span className="text-sm font-medium font-rubik text-gray-700">
          {sharing === 'facebook' ? 'Creating...' : 'Share on Facebook'}
        </span>
      </button>

      {/* Info footer */}
      <div className="px-4 py-2 border-t border-gray-100 mt-1">
        <p className="text-xs text-gray-400 font-rubik">
          📊 Captures current results at time of sharing
        </p>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="text-tertiary hover:text-primary text-sm font-medium flex items-center gap-1 font-rubik transition-colors"
        data-testid="share-poll-button"
      >
        <MdShare className="text-base" />
        Share
      </button>

      {dropdown}
    </div>
  );
}

export default ShareButton;
